import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import SectionHeader, { CTABar } from "@/components/site/SectionHeader";
import { SERVICES, INDUSTRIES, WHY } from "@/components/site/content";

const HERO_IMG = "https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg";

const METRICS = [
  { value: "400+", label: "Employees on Payroll" },
  { value: "30+", label: "Recruiters" },
  { value: "Multi", label: "Industries Served" },
  { value: "Pan-India", label: "Hiring Support" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white hero-grid-bg">
        <div className="container-tight grid gap-14 lg:grid-cols-[1.05fr,1fr] items-center pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="relative">
            <p className="overline mb-5">People · Process · Performance</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy-800">
              Building Stronger Workforces.<br />
              <span className="text-gold-500">Driving Better Business.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              End-to-end manpower, recruitment, payroll and workforce solutions designed to help businesses
              hire faster, operate efficiently and scale confidently.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/employers" data-testid="hero-cta-hire" className="btn-lift inline-flex justify-center items-center h-12 px-7 rounded-full bg-navy-800 hover:bg-navy-700 text-white font-medium">
                Hire With CRETRE <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="btn-lift inline-flex justify-center items-center h-12 px-7 rounded-full border border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white font-medium">
                Explore Our Services
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["Pan-India Hiring", "Payroll & Compliance", "Blue & White Collar", "Dedicated Recruiters"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-gold-500" />{t}</span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gold-100/50 rounded-3xl -rotate-2" />
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl shadow-navy-900/10">
              <img src={HERO_IMG} alt="CRETRE workforce solutions" className="w-full h-[420px] lg:h-[540px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-5 border border-border">
                <p className="overline">Trusted Partner</p>
                <p className="mt-1 font-serif text-navy-800 text-lg">Workforce solutions across India, headquartered in Ahmedabad.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust metrics */}
        <div className="container-tight pb-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border border border-border rounded-2xl bg-white">
            {METRICS.map((m, i) => (
              <div key={i} data-testid={`hero-metric-${i}`} className="p-6 lg:p-8 text-center">
                <div className="font-serif text-3xl lg:text-4xl text-navy-800">{m.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad bg-secondary/40">
        <div className="container-tight">
          <SectionHeader overline="What we do" title="End-to-End Workforce Solutions" subtitle="From frontline staffing to executive search — a single partner for every hiring and workforce need." />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <div key={s.key} data-testid={`home-service-${s.key}`} className="group bg-white rounded-2xl border border-border p-7 hover:shadow-xl hover:-translate-y-1 hover:border-gold-400/60 transition-all duration-300">
                <div className="h-12 w-12 grid place-items-center rounded-xl bg-navy-800 text-white group-hover:bg-gold-500 transition-colors">
                  <s.icon size={24} weight="duotone" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-navy-800">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <Link to="/services" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-800 hover:text-gold-600">
                  Learn More <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad">
        <div className="container-tight grid lg:grid-cols-[1fr,1.4fr] gap-14">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="overline mb-4">Why CRETRE</p>
            <h2 className="font-serif text-3xl lg:text-5xl text-navy-800 leading-[1.1]">Why Businesses Choose CRETRE</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              A single, reliable HR partner for recruitment, staffing, payroll and workforce management — built around your business realities.
            </p>
            <Link to="/about" data-testid="why-learn-more" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-800 hover:text-gold-600">
              About Our Approach <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {WHY.map((w, i) => (
              <div key={i} data-testid={`home-why-${i}`} className="border-l-2 border-gold-500 pl-5 py-2">
                <h4 className="font-serif text-xl text-navy-800">{w.title}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section-pad bg-navy-900 text-white">
        <div className="container-tight">
          <div className="grid lg:grid-cols-[1fr,auto] items-end gap-6">
            <div>
              <p className="overline text-gold-400 mb-4">Industries We Serve</p>
              <h2 className="font-serif text-3xl lg:text-5xl text-white leading-[1.1]">Experience across sectors that move India.</h2>
            </div>
            <Link to="/industries" className="text-sm text-gold-400 hover:text-white font-semibold inline-flex items-center gap-1.5">All Industries <ArrowRight size={14} /></Link>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <div key={ind.name} data-testid={`home-industry-${i}`} className="p-6 rounded-2xl border border-white/10 hover:border-gold-400/60 hover:bg-white/5 transition-all">
                <ind.icon size={28} weight="duotone" className="text-gold-400" />
                <p className="mt-4 font-medium text-white/90">{ind.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABar
        title="Let’s Build Your Workforce."
        text="Whether you need 5 employees or a large workforce, CRETRE can help you build and manage the right team."
        primary={{ to: "/employers", label: "Request Staffing Support" }}
        secondary={{ to: "/contact", label: "Talk to Our Team" }}
        testid="home-cta-bar"
      />
    </div>
  );
}
